import React from "react";

/**
 * ITE Event <select> with optgroup grouping once eventsWithData has loaded.
 *
 * Props
 *   value          string   — currently selected event ID, or "" for All/unset
 *   onChange       fn       — change handler (e) => void
 *   events         array    — full ITE events list from context
 *   eventsWithData Set|null — event IDs that have competitor data; null while loading
 *   style          object   — inline style for the <select>
 *   allowAll       bool     — if true, first option is a selectable "All Events";
 *                             if false (default), first option is a disabled placeholder
 *   placeholder    string   — label for the first option (overrides default)
 *
 * Grouping behaviour
 *   • While eventsWithData is null (still loading), shows a flat list with no grouping.
 *   • Once loaded, splits into "Events with competitor data" (enabled) and
 *     "No competitor data yet" (disabled).
 */
export function EventSelect({
  value,
  onChange,
  events,
  eventsWithData,
  style,
  allowAll = false,
  placeholder,
}) {
  const allEvents = events || [];
  const loaded    = eventsWithData !== null;

  const withData    = loaded ? allEvents.filter(e =>  eventsWithData.has(e.ite_event_id)) : allEvents;
  const withoutData = loaded ? allEvents.filter(e => !eventsWithData.has(e.ite_event_id)) : [];

  const firstLabel = placeholder || (allowAll ? "ITE Event: All" : "Select an ITE Event…");

  return (
    <select value={value} onChange={onChange} style={style}>
      {allowAll
        ? <option value="">{firstLabel}</option>
        : <option value="" disabled>{firstLabel}</option>
      }

      {loaded && withData.length > 0 && (
        <optgroup label="Events with competitor data">
          {withData.map(e => (
            <option key={e.ite_event_id} value={e.ite_event_id}>{e.name}</option>
          ))}
        </optgroup>
      )}

      {/* Flat list before eventsWithData loads so the dropdown is usable immediately */}
      {!loaded && allEvents.map(e => (
        <option key={e.ite_event_id} value={e.ite_event_id}>{e.name}</option>
      ))}

      {loaded && withoutData.length > 0 && (
        <optgroup label="No competitor data yet">
          {withoutData.map(e => (
            <option key={e.ite_event_id} value={e.ite_event_id} disabled>{e.name}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
